import { useRolexEventTracking } from '@lib/hooks/use-rolex-track-events'
import { useSyncRolexCmpTracking } from '@lib/hooks/use-sync-rolex-cmp-tracking'
import { useRolexTrackingScript } from '@lib/use-rolex-tracking-script-store'
import Script from 'next/script'
import { FC, useCallback, useEffect, useState } from 'react'
import { RolexPageProps } from '.'

type RolexPageTypeTrackingProps = RolexPageProps

export const ADOBE_TM_PRODUCTION =
  'https://assets.adobedtm.com/7e3b3fa0902e/db4a1bdd4eaa/launch-384bfb3a027c.min.js'
export const ADOBE_TM_STAGING =
  'https://assets.adobedtm.com/7e3b3fa0902e/db4a1bdd4eaa/launch-98ac5c6226e2-staging.min.js'

const environment = {
  environmentVersion: 'V7',
  coBrandedVersion: 'Bespoke',
}

// @ts-ignore
export const RolexPageTypeTracking: FC<RolexPageTypeTrackingProps> = (props) => {
  const { track } = useRolexEventTracking()
  const rolexConsent = useSyncRolexCmpTracking()
  const { setScriptIsLoaded, scriptIsLoaded } = useRolexTrackingScript()
  const [hasSetupDatalayer, setHasSetupDatalayer] = useState(false)
  const [hasTrackedPageView, setHasTrackedPageView] = useState(false)
  const adobeTrackingSrc =
    process.env.NEXT_PUBLIC_STAGE === 'production' ? ADOBE_TM_PRODUCTION : ADOBE_TM_STAGING

  const trackPageView = useCallback(() => {
    track('pageView')
    setHasTrackedPageView(true)
  }, [track])

  // 1. Make sure to populate window.digitalDataLayer with the correct data before loading the Rolex script,
  // otherwise the Rolex script will fire with incomplete data.
  useEffect(() => {
    const { pageType, productFamily, productRMC } = props

    const newDatalayer = {
      environment,
      page: {
        pageType,
        pageFamilyName: productFamily || undefined,
      },
      ...(productRMC && {
        products: {
          productRMC,
        },
      }),
    }

    if (JSON.stringify(window.digitalDataLayer) !== JSON.stringify(newDatalayer)) {
      window.digitalDataLayer = newDatalayer
      if (hasSetupDatalayer) {
        trackPageView()
      }
      setHasSetupDatalayer(true)
    }
  }, [hasSetupDatalayer, props, trackPageView])

  // 2. Track the page view event if the script is loaded and consent is given
  useEffect(() => {
    if (rolexConsent && scriptIsLoaded && !hasTrackedPageView) {
      trackPageView()
    }
  }, [rolexConsent, scriptIsLoaded, hasTrackedPageView, trackPageView])

  return (
    rolexConsent &&
    hasSetupDatalayer && (
      <Script
        strategy="lazyOnload"
        onLoad={() => {
          setScriptIsLoaded()
        }}
        src={adobeTrackingSrc}
      />
    )
  )
}
